/**
 * Insights Panel Component v2
 * 
 * Displays AI-generated trading insights with:
 * - Time range selector
 * - Prose response from Finance Bro AI (v2 format)
 * - Legacy insight cards (v1 format - backward compatible)
 * - Context metadata
 * - Loading/empty states
 */

import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Lightbulb, 
  AlertTriangle, 
  TrendingUp, 
  Target,
  RefreshCw,
  Calendar,
  BarChart3,
  Sparkles,
  Cpu,
  Brain
} from 'lucide-react';
import { DayPicker, type DateRange as DayPickerRange } from 'react-day-picker';
import 'react-day-picker/style.css';
import { format, parseISO } from 'date-fns';
import { useAI, TimeRange, AIInsight, isV2Response } from '../hooks/useAI';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { type: 'spring', stiffness: 300, damping: 30 }
  }
};

// Category icons and colors (for v1 format)
const categoryConfig: Record<string, { icon: React.ElementType; color: string; bgColor: string }> = {
  strategy_adjustment: { 
    icon: Target, 
    color: 'text-blue-600 dark:text-blue-400', 
    bgColor: 'bg-blue-50 dark:bg-blue-900/20' 
  },
  risk_warning: { 
    icon: AlertTriangle, 
    color: 'text-amber-600 dark:text-amber-400', 
    bgColor: 'bg-amber-50 dark:bg-amber-900/20' 
  },
  optimization: { 
    icon: TrendingUp, 
    color: 'text-emerald-600 dark:text-emerald-400', 
    bgColor: 'bg-emerald-50 dark:bg-emerald-900/20' 
  },
  portfolio_action: { 
    icon: Lightbulb, 
    color: 'text-purple-600 dark:text-purple-400', 
    bgColor: 'bg-purple-50 dark:bg-purple-900/20' 
  },
};

const timeRangeOptions: { value: TimeRange; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: '7 Days' },
  { value: 'month', label: '30 Days' },
  { value: 'all', label: 'All Time' },
];

/**
 * Simple formatter for AI response text
 * Adds basic styling for bold text and preserves line breaks
 */
function formatAIResponse(text: string): React.ReactNode {
  // Split by double newlines (paragraphs)
  const paragraphs = text.split(/\n\n+/);
  
  return paragraphs.map((para, idx) => {
    // Handle bold text (**text** or __text__)
    const parts = para.split(/(\*\*[^*]+\*\*|__[^_]+__)/g);
    const formattedParts = parts.map((part, partIdx) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={partIdx} className="font-semibold text-gray-900 dark:text-white">{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith('__') && part.endsWith('__')) {
        return <strong key={partIdx} className="font-semibold text-gray-900 dark:text-white">{part.slice(2, -2)}</strong>;
      }
      // Handle single line breaks within paragraph
      return part.split('\n').map((line, lineIdx, arr) => (
        <React.Fragment key={`${partIdx}-${lineIdx}`}>
          {line}
          {lineIdx < arr.length - 1 && <br />}
        </React.Fragment>
      ));
    });
    
    return (
      <p key={idx} className="mb-3 last:mb-0">
        {formattedParts}
      </p>
    );
  });
}

export function InsightsPanel() {
  const {
    insights,
    insightsLoading,
    insightsError,
    timeRange,
    setTimeRange,
    customDateRange,
    setCustomDateRange,
    deepThinking,
    setDeepThinking,
    refetchInsights,
    isConfigured,
  } = useAI();

  const [customPickerOpen, setCustomPickerOpen] = useState(false);
  const [pickerRange, setPickerRange] = useState<DayPickerRange | undefined>(() => {
    if (!customDateRange) return undefined;
    return { from: customDateRange.from, to: customDateRange.to };
  });

  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  useEffect(() => {
    if (!insightsLoading) {
      setElapsedSeconds(0);
      return;
    }
    setElapsedSeconds(0);
    const t = setInterval(() => setElapsedSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [insightsLoading]);

  // Keep picker selection in sync if customDateRange is updated externally
  useEffect(() => {
    if (!customDateRange) {
      setPickerRange(undefined);
      return;
    }
    setPickerRange({ from: customDateRange.from, to: customDateRange.to });
  }, [customDateRange]);

  const formattedCustomLabel = useMemo(() => {
    if (!insights || !isV2Response(insights)) return null;
    if (!insights.dateRange?.start || !insights.dateRange?.end) return null;
    if (insights.dateRange.label !== 'Custom range') return null;
    try {
      const start = parseISO(insights.dateRange.start);
      const end = parseISO(insights.dateRange.end);
      return `${format(start, 'MMM d, yyyy')} – ${format(end, 'MMM d, yyyy')}`;
    } catch {
      return null;
    }
  }, [insights]);

  const canApplyCustom = !!pickerRange?.from && !!pickerRange?.to;

  function applyCustomRange() {
    if (!pickerRange?.from || !pickerRange?.to) return;
    setCustomDateRange({ from: pickerRange.from, to: pickerRange.to });
    setCustomPickerOpen(false);
  }

  function clearCustomRange() {
    setCustomDateRange(null);
    setPickerRange(undefined);
  }

  function selectPreset(next: TimeRange) {
    setTimeRange(next);
    // Presets override custom selection
    if (customDateRange) clearCustomRange();
    setCustomPickerOpen(false);
  }

  if (!isConfigured) {
    return (
      <div className="p-6 text-center">
        <Lightbulb className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500 dark:text-gray-400">
          AI insights not available. Contact support to enable.
        </p>
      </div>
    );
  }

  // Determine which format we have
  const isV2 = insights && isV2Response(insights);
  const hasInsights = insights && (
    isV2 
      ? insights.response && insights.response.length > 0
      : 'insights' in insights && insights.insights.length > 0
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Lightbulb className="w-5 h-5 text-indigo-500" />
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">
            Insights
          </h2>
        </div>
        <button
          onClick={() => refetchInsights()}
          disabled={insightsLoading}
          className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors disabled:opacity-50"
          title="Refresh insights"
        >
          <RefreshCw className={`w-4 h-4 ${insightsLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Time Range Selector */}
      <div className="flex gap-2 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg">
        {timeRangeOptions.map((option) => (
          <button
            key={option.value}
            onClick={() => selectPreset(option.value)}
            className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
              timeRange === option.value
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* Custom Date Range + Deep Thinking */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCustomPickerOpen((v) => !v)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
              customDateRange
                ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
            }`}
            title="Pick a custom date range"
          >
            <Calendar className="w-4 h-4" />
            Custom
          </button>
          {customDateRange && (
            <button
              onClick={clearCustomRange}
              className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            >
              Clear
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setDeepThinking((v) => !v)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
              deepThinking
                ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
            }`}
            title="Use deeper reasoning (slower)"
          >
            <Brain className="w-4 h-4" />
            Deep Thinking
          </button>
          {deepThinking && (
            <span className="text-xs text-amber-600 dark:text-amber-400">
              Takes ~3–5 min
            </span>
          )}
        </div>
      </div>

      {/* Collapsible Date Picker */}
      {customPickerOpen && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
        >
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              {pickerRange?.from && pickerRange?.to
                ? `${format(pickerRange.from, 'MMM d, yyyy')} – ${format(pickerRange.to, 'MMM d, yyyy')}`
                : 'Select a start and end date'}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCustomPickerOpen(false)}
                className="px-3 py-1.5 text-sm rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200"
              >
                Close
              </button>
              <button
                onClick={applyCustomRange}
                disabled={!canApplyCustom}
                className="px-3 py-1.5 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white disabled:cursor-not-allowed"
              >
                Apply
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <DayPicker
              mode="range"
              numberOfMonths={2}
              selected={pickerRange}
              onSelect={setPickerRange}
            />
          </div>
        </motion.div>
      )}

      {/* Context Metadata */}
      {insights && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex items-center gap-4 px-3 py-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg text-sm"
        >
          <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
            <Calendar className="w-4 h-4" />
            <span>
              {isV2
                ? (formattedCustomLabel ?? insights.dateRange.label)
                : insights.context?.timeRange?.label || timeRange}
            </span>
          </div>
          <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
            <BarChart3 className="w-4 h-4" />
            <span>
              {isV2 ? insights.tradeCount : insights.context?.closedTrades || 0} trades analyzed
            </span>
          </div>
          {isV2 && insights.model && (
            <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-500">
              <Cpu className="w-3.5 h-3.5" />
              <span className="text-xs">{insights.model}</span>
            </div>
          )}
        </motion.div>
      )}

      {/* Loading State */}
      {insightsLoading && (
        <div className="space-y-3">
          <div className="p-6 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-lg animate-pulse">
            <div className="flex items-center gap-2 mb-4">
              {deepThinking ? (
                <Brain className="w-5 h-5 text-purple-400 animate-pulse" />
              ) : (
                <Sparkles className="w-5 h-5 text-indigo-400 animate-pulse" />
              )}
              <span className={`text-sm ${deepThinking ? 'text-purple-700 dark:text-purple-300' : 'text-indigo-600 dark:text-indigo-400'}`}>
                {deepThinking ? 'Deep analysis in progress...' : 'AI is analyzing your trades...'}
                <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">({elapsedSeconds}s)</span>
              </span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              {deepThinking ? 'Typically takes ~3–5 minutes' : 'Typically takes ~15–30 seconds'}
            </div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full" />
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6" />
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-4/6" />
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mt-4" />
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
            </div>
          </div>
        </div>
      )}

      {/* Error State */}
      {insightsError && (
        (() => {
          const errorMsg = insightsError instanceof Error ? insightsError.message : '';
          const isNoData = errorMsg === 'No data' || errorMsg.includes('not seeing any closed trades');
          
          if (isNoData) {
            // Friendly "no trades" state instead of error
            return (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="p-8 text-center"
              >
                <div className="w-16 h-16 mx-auto mb-4 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
                  <Calendar className="w-8 h-8 text-amber-500" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  No trades found
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs mx-auto">
                  No closed trades in this date range. Try selecting a different time period or check back after making some trades.
                </p>
              </motion.div>
            );
          }
          
          // Generic error for other failures
          return (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">
                Failed to load insights. Please try again.
              </p>
              <p className="text-xs text-red-500 dark:text-red-500 mt-1">
                {errorMsg || 'Unknown error'}
              </p>
            </div>
          );
        })()
      )}

      {/* Empty State */}
      {!insightsLoading && !insightsError && !hasInsights && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="p-8 text-center"
        >
          <div className="w-16 h-16 mx-auto mb-4 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center">
            <BarChart3 className="w-8 h-8 text-indigo-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            More data needed
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs mx-auto">
            Complete at least 10 trades to unlock AI-powered insights about your trading patterns.
          </p>
        </motion.div>
      )}

      {/* V2 Prose Response */}
      {!insightsLoading && !insightsError && isV2 && insights.response && (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-3"
        >
          <motion.div
            variants={itemVariants}
            className="p-5 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-gray-800 dark:to-gray-800 rounded-lg border border-indigo-100 dark:border-gray-700"
          >
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-indigo-500" />
              <span className="text-sm font-medium text-indigo-700 dark:text-indigo-400">
                AI Trade Analyst
              </span>
            </div>
            <div className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">
              {formatAIResponse(insights.response)}
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* V1 Legacy Insights List */}
      {!insightsLoading && !insightsError && insights && !isV2 && 'insights' in insights && insights.insights.length > 0 && (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-3"
        >
          {insights.insights.map((insight) => (
            <InsightCard key={insight.id} insight={insight} />
          ))}
        </motion.div>
      )}
    </div>
  );
}

function InsightCard({ insight }: { insight: AIInsight }) {
  const config = categoryConfig[insight.category] || categoryConfig.optimization;
  const Icon = config.icon;

  return (
    <motion.div
      variants={itemVariants}
      whileHover={{ scale: 1.01 }}
      className={`p-4 rounded-lg border border-gray-200 dark:border-gray-700 ${config.bgColor}`}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg bg-white dark:bg-gray-800 ${config.color}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-gray-900 dark:text-white">
            {insight.title}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {insight.summary}
          </p>
          {insight.recommendation && insight.recommendation !== insight.summary && (
            <p className="text-sm text-gray-700 dark:text-gray-300 mt-2 font-medium">
              → {insight.recommendation}
            </p>
          )}
          <div className="flex items-center gap-4 mt-3 text-xs text-gray-500 dark:text-gray-500">
            <span>Based on: {insight.dataBasis}</span>
            {insight.estimatedImpact && (
              <span className="text-emerald-600 dark:text-emerald-400 font-medium">
                {insight.estimatedImpact}
              </span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
